import Box, {type BoxProps} from '@mui/material/Box';
import {type PopperProps} from '@mui/material/Popper';
import MuiTooltip, {type TooltipProps as MuiTooltipProps} from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import type * as React from 'react';

export interface TooltipProps {
  title?: React.ReactNode;
  children: React.ReactNode;
  followCursor?: boolean;
  placement?: MuiTooltipProps['placement'];
  sx?: PopperProps['sx'];
  contentSx?: Required<BoxProps['sx']>;
  leaveDelay?: number;
  wrapText?: boolean;
  disablePortal?: boolean;
  /** Lets keyboard users focus the trigger to open the tooltip. Requires an `ariaLabel`. */
  focusable?: boolean;
  /** Accessible name for a focusable trigger, such as "More information". */
  ariaLabel?: string;
}

const styles = {
  tooltip: {
    p: 1.2,
    whiteSpace: 'pre-line',
    backgroundColor: 'background.paper',
    color: 'text.primary',
    boxShadow: 8,
  },
  noWrapTooltip: {
    maxWidth: 'none',
  },
  arrow: {
    color: 'background.paper',
  },
};

function Tooltip({
  title,
  children,
  followCursor,
  placement = 'top',
  sx = {},
  contentSx = {},
  leaveDelay,
  wrapText = true,
  disablePortal,
  focusable,
  ariaLabel,
}: TooltipProps) {
  if (title) {
    return (
      <MuiTooltip
        arrow
        title={<Typography variant="body2">{title}</Typography>}
        followCursor={followCursor}
        placement={placement}
        leaveDelay={leaveDelay}
        slotProps={{
          popper: {sx, disablePortal},
          tooltip: {
            sx: [styles.tooltip, !wrapText && styles.noWrapTooltip],
          },
          arrow: {sx: styles.arrow},
        }}
      >
        {/* Box is needed for disabling buttons with tooltips, see https://mui.com/material-ui/react-tooltip/#disabled-elements */}
        {/* Box can also hold a ref to which the tooltip will need to anchor itself,
          whereas a child component passed in may not, see https://mui.com/material-ui/guides/composition/#caveat-with-refs */}
        <Box
          sx={contentSx}
          component="span"
          {...(focusable && {tabIndex: 0, role: 'button', 'aria-label': ariaLabel})}
        >
          {children}
        </Box>
      </MuiTooltip>
    );
  }

  return <>{children}</>;
}

export default Tooltip;
